<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import TicketTypesAPI from 'dashboard/api/ticketTypes';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const { t } = useI18n();
const store = useStore();
const teams = useMapGetter('teams/getTeams');

const ticketTypes = ref([]);
const isLoading = ref(true);
const isSaving = ref(false);
const editingType = ref(null);

const categories = ['customer', 'back_office', 'tracker'];
const categoryIcons = {
  customer: 'i-lucide-ticket',
  back_office: 'i-lucide-lock-keyhole',
  tracker: 'i-lucide-git-merge',
};
const categoryTitle = category =>
  ({
    customer: t('TICKET_TYPES_SETTINGS.CATEGORIES.CUSTOMER.TITLE'),
    back_office: t('TICKET_TYPES_SETTINGS.CATEGORIES.BACK_OFFICE.TITLE'),
    tracker: t('TICKET_TYPES_SETTINGS.CATEGORIES.TRACKER.TITLE'),
  })[category];
const categoryDescription = category =>
  ({
    customer: t('TICKET_TYPES_SETTINGS.CATEGORIES.CUSTOMER.DESCRIPTION'),
    back_office: t('TICKET_TYPES_SETTINGS.CATEGORIES.BACK_OFFICE.DESCRIPTION'),
    tracker: t('TICKET_TYPES_SETTINGS.CATEGORIES.TRACKER.DESCRIPTION'),
  })[category];

const emptyForm = () => ({
  id: null,
  name: '',
  description: '',
  category: 'customer',
  icon: '',
  active: true,
  default_team_id: '',
  ticket_count: 0,
  states: [
    {
      name: t('TICKET_TYPES_SETTINGS.STATES.SUBMITTED'),
      category: 'submitted',
      customer_visible: true,
      terminal: false,
    },
    {
      name: t('TICKET_TYPES_SETTINGS.STATES.IN_PROGRESS'),
      category: 'in_progress',
      customer_visible: true,
      terminal: false,
    },
    {
      name: t('TICKET_TYPES_SETTINGS.STATES.WAITING'),
      category: 'waiting_on_customer',
      customer_visible: true,
      terminal: false,
    },
    {
      name: t('TICKET_TYPES_SETTINGS.STATES.RESOLVED'),
      category: 'resolved',
      customer_visible: true,
      terminal: true,
    },
  ],
  attribute_definitions: [],
});

const groupedTypes = computed(() =>
  categories.map(category => ({
    category,
    records: ticketTypes.value.filter(type => type.category === category),
  }))
);

const fetchTicketTypes = async () => {
  isLoading.value = true;
  try {
    const { data } = await TicketTypesAPI.get();
    ticketTypes.value = data;
  } catch (error) {
    useAlert(t('TICKET_TYPES_SETTINGS.API.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const openCreate = () => {
  editingType.value = emptyForm();
};

const openEdit = type => {
  editingType.value = JSON.parse(JSON.stringify(type));
};

const closeEditor = () => {
  editingType.value = null;
};

const addState = () => {
  editingType.value.states.push({
    name: '',
    category: 'in_progress',
    customer_visible: editingType.value.category === 'customer',
    terminal: false,
  });
};

const removeState = index => {
  editingType.value.states.splice(index, 1);
};

const addAttribute = () => {
  editingType.value.attribute_definitions.push({
    key: '',
    label: '',
    data_type: 'text',
    required: false,
    customer_visible: editingType.value.category === 'customer',
    configuration: {},
  });
};

const removeAttribute = index => {
  editingType.value.attribute_definitions.splice(index, 1);
};

const saveType = async () => {
  isSaving.value = true;
  const payload = {
    ticket_type: {
      ...editingType.value,
      default_team_id: editingType.value.default_team_id || null,
      states: editingType.value.states.map(state => ({
        ...state,
        customer_visible:
          editingType.value.category === 'customer' && state.customer_visible,
      })),
      attribute_definitions: editingType.value.attribute_definitions.map(
        attribute => ({
          ...attribute,
          customer_visible:
            editingType.value.category === 'customer' &&
            attribute.customer_visible,
        })
      ),
    },
  };

  try {
    if (editingType.value.id) {
      await TicketTypesAPI.update(editingType.value.id, payload);
    } else {
      await TicketTypesAPI.create(payload);
    }
    await fetchTicketTypes();
    closeEditor();
    useAlert(t('TICKET_TYPES_SETTINGS.API.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('TICKET_TYPES_SETTINGS.API.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const archiveType = async type => {
  try {
    await TicketTypesAPI.delete(type.id);
    await fetchTicketTypes();
    closeEditor();
    useAlert(t('TICKET_TYPES_SETTINGS.API.ARCHIVE_SUCCESS'));
  } catch (error) {
    useAlert(t('TICKET_TYPES_SETTINGS.API.ARCHIVE_ERROR'));
  }
};

onMounted(() => {
  store.dispatch('teams/get');
  fetchTicketTypes();
});
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="$t('TICKET_TYPES_SETTINGS.TITLE')"
        :description="$t('TICKET_TYPES_SETTINGS.DESCRIPTION')"
      >
        <template #actions>
          <Button
            :label="$t('TICKET_TYPES_SETTINGS.CREATE')"
            icon="i-lucide-plus"
            size="sm"
            @click="openCreate"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="border-y border-n-weak">
        <section
          v-for="group in groupedTypes"
          :key="group.category"
          class="border-b border-n-weak last:border-b-0"
        >
          <header class="flex items-start gap-3 bg-n-alpha-1 px-4 py-3">
            <span
              class="flex size-8 shrink-0 items-center justify-center rounded-md bg-n-alpha-2"
            >
              <Icon
                :icon="categoryIcons[group.category]"
                class="size-4 text-n-slate-11"
              />
            </span>
            <span class="min-w-0">
              <span class="block text-sm font-semibold text-n-slate-12">
                {{ categoryTitle(group.category) }}
              </span>
              <span class="block text-xs text-n-slate-10">
                {{ categoryDescription(group.category) }}
              </span>
            </span>
          </header>

          <div
            v-if="!group.records.length"
            class="px-4 py-6 text-sm text-n-slate-10"
          >
            {{ $t('TICKET_TYPES_SETTINGS.EMPTY') }}
          </div>
          <button
            v-for="type in group.records"
            :key="type.id"
            type="button"
            class="grid w-full grid-cols-[minmax(0,1fr)_10rem_7rem_2rem] items-center gap-4 border-t border-n-weak px-4 py-3 text-left first:border-t-0 hover:bg-n-alpha-1"
            @click="openEdit(type)"
          >
            <span class="min-w-0">
              <span class="block truncate text-sm font-medium text-n-slate-12">
                {{ type.name }}
              </span>
              <span class="block truncate text-xs text-n-slate-10">{{
                type.description
              }}</span>
            </span>
            <span class="truncate text-sm text-n-slate-11">
              {{
                type.default_team?.name || $t('TICKET_TYPES_SETTINGS.NO_TEAM')
              }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{
                $t('TICKET_TYPES_SETTINGS.STATE_COUNT', {
                  count: type.states.length,
                })
              }}
            </span>
            <Icon icon="i-lucide-chevron-right" class="size-4 text-n-slate-9" />
          </button>
        </section>
      </div>
    </template>
  </SettingsLayout>

  <div
    v-if="editingType"
    class="fixed inset-0 z-50 flex justify-end bg-n-alpha-4"
    @click.self="closeEditor"
  >
    <form
      class="flex h-full w-full max-w-2xl flex-col bg-n-surface-1 shadow-xl"
      @submit.prevent="saveType"
    >
      <header
        class="flex items-center justify-between border-b border-n-weak px-6 py-4"
      >
        <div>
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{
              editingType.id
                ? $t('TICKET_TYPES_SETTINGS.EDITOR.EDIT')
                : $t('TICKET_TYPES_SETTINGS.EDITOR.NEW')
            }}
          </h2>
          <p class="text-sm text-n-slate-10">
            {{ $t('TICKET_TYPES_SETTINGS.EDITOR.DESCRIPTION') }}
          </p>
        </div>
        <Button
          icon="i-lucide-x"
          variant="ghost"
          color="slate"
          type="button"
          @click="closeEditor"
        />
      </header>

      <div class="flex-1 space-y-7 overflow-y-auto px-6 py-5">
        <section class="grid gap-4 sm:grid-cols-2">
          <label class="space-y-1.5 text-sm text-n-slate-11 sm:col-span-2">
            <span>{{ $t('TICKET_TYPES_SETTINGS.EDITOR.NAME') }}</span>
            <input
              v-model="editingType.name"
              required
              maxlength="100"
              class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12 outline-none focus:border-n-brand"
            />
          </label>
          <label class="space-y-1.5 text-sm text-n-slate-11 sm:col-span-2">
            <span>{{
              $t('TICKET_TYPES_SETTINGS.EDITOR.DESCRIPTION_LABEL')
            }}</span>
            <textarea
              v-model="editingType.description"
              maxlength="500"
              rows="3"
              class="w-full resize-none rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-n-slate-12 outline-none focus:border-n-brand"
            />
          </label>
          <label class="space-y-1.5 text-sm text-n-slate-11">
            <span>{{ $t('TICKET_TYPES_SETTINGS.EDITOR.CATEGORY') }}</span>
            <select
              v-model="editingType.category"
              :disabled="Boolean(editingType.id)"
              class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
            >
              <option
                v-for="category in categories"
                :key="category"
                :value="category"
              >
                {{ categoryTitle(category) }}
              </option>
            </select>
          </label>
          <label class="space-y-1.5 text-sm text-n-slate-11">
            <span>{{ $t('TICKET_TYPES_SETTINGS.EDITOR.DEFAULT_TEAM') }}</span>
            <select
              v-model="editingType.default_team_id"
              class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 capitalize text-n-slate-12"
            >
              <option value="">
                {{ $t('TICKET_TYPES_SETTINGS.NO_TEAM') }}
              </option>
              <option v-for="team in teams" :key="team.id" :value="team.id">
                {{ team.name }}
              </option>
            </select>
          </label>
        </section>

        <section class="space-y-3">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('TICKET_TYPES_SETTINGS.EDITOR.STATES') }}
              </h3>
              <p class="text-xs text-n-slate-10">
                {{ $t('TICKET_TYPES_SETTINGS.EDITOR.STATES_DESCRIPTION') }}
              </p>
            </div>
            <Button
              :label="$t('TICKET_TYPES_SETTINGS.EDITOR.ADD_STATE')"
              icon="i-lucide-plus"
              size="xs"
              variant="ghost"
              color="slate"
              type="button"
              @click="addState"
            />
          </div>
          <div
            v-for="(state, index) in editingType.states"
            :key="index"
            class="grid grid-cols-[1fr_10rem_2rem] gap-2"
          >
            <input
              v-model="state.name"
              required
              class="h-9 rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
            />
            <select
              v-model="state.category"
              class="h-9 rounded-md border border-n-weak bg-n-alpha-1 px-2 text-sm text-n-slate-12"
              @change="state.terminal = state.category === 'resolved'"
            >
              <option value="submitted">
                {{ $t('TICKET_TYPES_SETTINGS.STATES.SUBMITTED') }}
              </option>
              <option value="in_progress">
                {{ $t('TICKET_TYPES_SETTINGS.STATES.IN_PROGRESS') }}
              </option>
              <option value="waiting_on_customer">
                {{ $t('TICKET_TYPES_SETTINGS.STATES.WAITING') }}
              </option>
              <option value="resolved">
                {{ $t('TICKET_TYPES_SETTINGS.STATES.RESOLVED') }}
              </option>
            </select>
            <Button
              icon="i-lucide-trash-2"
              variant="ghost"
              color="ruby"
              size="xs"
              type="button"
              @click="removeState(index)"
            />
          </div>
        </section>

        <section class="space-y-3">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('TICKET_TYPES_SETTINGS.EDITOR.ATTRIBUTES') }}
              </h3>
              <p class="text-xs text-n-slate-10">
                {{ $t('TICKET_TYPES_SETTINGS.EDITOR.ATTRIBUTES_DESCRIPTION') }}
              </p>
            </div>
            <Button
              :label="$t('TICKET_TYPES_SETTINGS.EDITOR.ADD_ATTRIBUTE')"
              icon="i-lucide-plus"
              size="xs"
              variant="ghost"
              color="slate"
              type="button"
              @click="addAttribute"
            />
          </div>
          <div
            v-for="(attribute, index) in editingType.attribute_definitions"
            :key="index"
            class="grid grid-cols-[1fr_1fr_8rem_2rem] gap-2"
          >
            <input
              v-model="attribute.label"
              required
              :placeholder="$t('TICKET_TYPES_SETTINGS.EDITOR.ATTRIBUTE_LABEL')"
              class="h-9 rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
            />
            <input
              v-model="attribute.key"
              required
              pattern="[a-z][a-z0-9_]*"
              :placeholder="$t('TICKET_TYPES_SETTINGS.EDITOR.ATTRIBUTE_KEY')"
              class="h-9 rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
            />
            <select
              v-model="attribute.data_type"
              class="h-9 rounded-md border border-n-weak bg-n-alpha-1 px-2 text-sm text-n-slate-12"
            >
              <option value="text">
                {{ $t('TICKET_TYPES_SETTINGS.DATA_TYPES.TEXT') }}
              </option>
              <option value="number">
                {{ $t('TICKET_TYPES_SETTINGS.DATA_TYPES.NUMBER') }}
              </option>
              <option value="boolean">
                {{ $t('TICKET_TYPES_SETTINGS.DATA_TYPES.BOOLEAN') }}
              </option>
              <option value="date">
                {{ $t('TICKET_TYPES_SETTINGS.DATA_TYPES.DATE') }}
              </option>
            </select>
            <Button
              icon="i-lucide-trash-2"
              variant="ghost"
              color="ruby"
              size="xs"
              type="button"
              @click="removeAttribute(index)"
            />
          </div>
        </section>
      </div>

      <footer
        class="flex items-center justify-between border-t border-n-weak px-6 py-4"
      >
        <Button
          v-if="editingType.id && editingType.active"
          :label="$t('TICKET_TYPES_SETTINGS.ARCHIVE')"
          variant="ghost"
          color="ruby"
          type="button"
          @click="archiveType(editingType)"
        />
        <span v-else />
        <div class="flex gap-2">
          <Button
            :label="$t('TICKET_TYPES_SETTINGS.CANCEL')"
            variant="ghost"
            color="slate"
            type="button"
            @click="closeEditor"
          />
          <Button
            :label="$t('TICKET_TYPES_SETTINGS.SAVE')"
            :is-loading="isSaving"
            type="submit"
          />
        </div>
      </footer>
    </form>
  </div>
</template>
